import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { firebaseApp } from "../../firebase";
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider, updateProfile } from "firebase/auth";
import toast from "react-hot-toast";
import {
  getFirestore,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit,
  setDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Share2,
  MoreVertical,
  MapPin,
  Calendar,
  Edit3,
  X,
  Save,
  Upload,
  Mail,
  Phone,
  User as UserIcon,
  Award,
  Briefcase,
  GraduationCap,
  Link as LinkIcon,
  Github,
  Linkedin,
  Twitter,
  Globe,
  FileText,
  AlertTriangle,
  Pencil,
  Brain,
  Flame,
  Trophy,
} from "lucide-react";
import VerifiedBadge from "../../components/VerifiedBadge";
import { useVerificationStatus } from "../../hooks/useVerificationStatus";
import { useUserProgress, useUserAchievements, useActivityTracker } from "../../hooks/useGamification";
import { safeToDate } from "../../utils/SafeToDate";
import LevelProgressBar from "../components/LevelProgressBar";
import StreakCounter from "../components/StreakCounter";
import Leaderboard from "../components/Leaderboard";
import AchievementGrid from "../components/AchievementGrid";
import AchievementModal from "../components/AchievementModal";

const NewProfile = () => {
  const { user, setUser, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);
  const storage = getStorage(firebaseApp);
  const { isVerified } = useVerificationStatus();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("reviews");
  const [experiences, setExperiences] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [recentTests, setRecentTests] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = React.useRef(null);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    username: "",
    title: "",
    location: "",
  });
  const [stats, setStats] = useState({
    testsCompleted: 0,
    interviewsDone: 0,
    successRate: 0
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [selectedAchievement, setSelectedAchievement] = useState(null);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [showFullLeaderboard, setShowFullLeaderboard] = useState(false);
  const [showAllAchievements, setShowAllAchievements] = useState(false);
  const [showBannerEditModal, setShowBannerEditModal] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [bannerImageUrl, setBannerImageUrl] = useState(null);
  const [showBannerMenu, setShowBannerMenu] = useState(false);
  const bannerFileInputRef = useRef(null);

  // Sync banner image URL when profile data changes
  useEffect(() => {
    if (profileData?.bannerImage) {
      setBannerImageUrl(profileData.bannerImage);
    }
  }, [profileData?.bannerImage]);

  // Gamification Hooks
  const { progress: userProgress, loading: progressLoading } = useUserProgress(user?.id || '');
  const { achievements: userAchievements, loading: achievementsLoading } = useUserAchievements(user?.id || '');
  const { trackActivity } = useActivityTracker(user?.id || '');

  // Extract social media links from resume data
  const getSocialMediaLinks = () => {
    const resumeData = profileData?.resumeData || {};
    const socialInfo = resumeData.socialMedia || {};

    return {
      github: socialInfo.github || resumeData.github || "",
      linkedin: socialInfo.linkedin || resumeData.linkedin || "",
      twitter: socialInfo.twitter || resumeData.twitter || "",
      portfolio: socialInfo.portfolio || resumeData.portfolio || "",
    };
  };

  // Extract projects from resume (handles both old and new data structures)
  const getProjects = () => {
    const resumeData = profileData?.resumeData || {};
    const professionalInfo = resumeData.professionalInfo || {};

    // Try multiple locations for compatibility with old and new data structures
    const projects =
      resumeData.projects ||  // New structure
      resumeData.additionalInfo?.projects ||  // New nested structure
      professionalInfo.projects ||  // Might be in professionalInfo
      [];

    console.log("📁 PROJECTS DEBUG:", {
      resumeDataProjects: resumeData.projects?.length || 0,
      additionalInfoProjects: resumeData.additionalInfo?.projects?.length || 0,
      professionalInfoProjects: professionalInfo.projects?.length || 0,
      finalCount: projects?.length || 0,
      finalData: projects
    });
    return Array.isArray(projects) ? projects : [];
  };

  // Extract work history (handles both old and new data structures)
  const getWorkHistory = () => {
    const resumeData = profileData?.resumeData || {};
    const professionalInfo = resumeData.professionalInfo || {};

    // Try multiple locations for compatibility
    const workHistory =
      resumeData.workHistory ||  // Primary location
      resumeData.workExperience ||  // Alternative location
      professionalInfo.experience ||  // Might be under professionalInfo
      [];

    console.log("💼 WORK HISTORY DEBUG:", {
      resumeDataWorkHistory: resumeData.workHistory?.length || 0,
      resumeDataWorkExperience: resumeData.workExperience?.length || 0,
      professionalInfoExperience: professionalInfo.experience?.length || 0,
      finalCount: workHistory?.length || 0,
      finalData: workHistory
    });
    return Array.isArray(workHistory) ? workHistory : [];;
  };

  // Extract education (handles both old and new data structures)
  const getEducation = () => {
    const resumeData = profileData?.resumeData || {};
    const professionalInfo = resumeData.professionalInfo || {};

    // Try multiple locations - education might be array, string, or nested
    let education =
      resumeData.education ||  // New array structure
      resumeData.additionalInfo?.education ||  // New nested structure
      professionalInfo.education ||  // Might be in professionalInfo
      [];

    // If education is a string from old structure, convert to array
    if (typeof education === 'string' && education.length > 0) {
      education = [{
        degree: education,
        institution: 'Education',
        year: ''
      }];
    }

    console.log("Education from resume:", education);
    return education;
  };

  // Extract certifications (handles both old and new data structures)
  const getCertifications = () => {
    const resumeData = profileData?.resumeData || {};
    const additionalInfo = resumeData.additionalInfo || {};

    // Try multiple locations for compatibility
    const certifications =
      resumeData.certifications ||  // Might be at root level (old structure)
      additionalInfo.certifications ||  // New nested structure
      [];

    console.log("Certifications from resume:", certifications);
    return certifications;
  };

  // Extract languages (handles both old and new data structures)
  const getLanguages = () => {
    const resumeData = profileData?.resumeData || {};
    const additionalInfo = resumeData.additionalInfo || {};

    // Try multiple locations for compatibility
    const languages =
      resumeData.languages ||  // Might be at root level
      additionalInfo.languages ||  // New nested structure
      [];

    console.log("Languages from resume:", languages);
    return languages;
  };

  // Extract skills from resume (handles both old and new data structures)
  const getSkills = () => {
    const resumeData = profileData?.resumeData || {};
    const professionalInfo = resumeData.professionalInfo || {};

    // Try multiple locations for compatibility with old and new structures
    const skills =
      profileData?.skills ||  // Extracted during profile load
      resumeData.professionalInfo?.skills ||  // New structure in professionalInfo
      resumeData.skills ||  // Might be at root level
      professionalInfo.skills ||  // Alternative location
      [];

    console.log("🎯 SKILLS DEBUG:", {
      profileDataSkills: profileData?.skills?.length || 0,
      professionalInfoSkills: professionalInfo.skills?.length || 0,
      resumeDataSkills: resumeData.skills?.length || 0,
      finalSkillsCount: skills?.length || 0,
      finalSkillsArray: skills
    });
    return Array.isArray(skills) ? skills : [];
  };

  // Load and live-sync profile data (updates when resumeData changes)
  useEffect(() => {
    if (!user?.id) return;

    let initialLoaded = false;
    const unsub = onSnapshot(doc(db, "userProfiles", user.id), async (profileSnap) => {
      try {
        const profile = profileSnap.exists() ? profileSnap.data() : {};
        const jobseekerSnap = await getDoc(doc(db, "jobseekers", user.id));
        const jobseeker = jobseekerSnap.exists() ? jobseekerSnap.data() : {};

        const resumeData = profile.resumeData || {};
        const resumeInfo = resumeData.personalInfo || {};
        const professionalInfo = resumeData.professionalInfo || {};

        // Extract skills - try multiple locations
        const skillsArray =
          professionalInfo.skills ||  // New/normalized structure
          resumeData.skills ||  // Root level
          [];

        const data = {
          name: profile.name || resumeInfo.fullName || jobseeker.name || user.name || "User",
          username: profile.username || `user_${user.id.slice(0, 6)}`,
          title: profile.title || professionalInfo.jobTitle || "Job Seeker",
          location: profile.location || resumeInfo.city || jobseeker.location || "Location not set",
          joinDate: profile.createdAt || jobseeker.createdAt || new Date(),
          bio: profile.bio || resumeInfo.summary || "",
          avatar: user.photoURL || null,
          bannerImage: profile.bannerImage || null,
          skills: skillsArray,
          resumeData,
        };

        console.log("=== PROFILE DATA LOADING ===");
        console.log("Profile loaded:", data);
        console.log("Resume data full structure:", resumeData);
        console.log("Professional info:", professionalInfo);
        console.log("Skills extracted:", skillsArray);
        console.log("Work history available:", resumeData.workHistory?.length > 0);
        console.log("Projects available:", resumeData.projects?.length > 0);
        console.log("================================");

        setProfileData(data);
        // Only reset edit form on first load to avoid overriding user edits
        if (!initialLoaded) {
          setEditFormData({
            name: data.name,
            username: data.username,
            title: data.title,
            location: data.location,
            bio: data.bio,
          });
          initialLoaded = true;
        }
      } catch (error) {
        console.error("Error syncing profile:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [user, db]);

  // Load experience data
  useEffect(() => {
    const loadExperiences = async () => {
      if (!user?.id) return;

      try {
        // Query experiences - simple query without orderBy to avoid index requirement
        const experiencesRef = collection(db, "experiences");
        const q = query(
          experiencesRef,
          where("userId", "==", user.id)
        );

        const snapshot = await getDocs(q);
        // Sort client-side if needed
        const exp = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const aDate = new Date(a.startDate || 0).getTime();
            const bDate = new Date(b.startDate || 0).getTime();
            return bDate - aDate; // desc order
          })
          .slice(0, 5);

        console.log("✅ Experiences loaded:", exp.length);
        setExperiences(exp);
      } catch (error) {
        console.error("❌ Error loading experiences:", error);
        setExperiences([]);
      }
    };

    loadExperiences();
  }, [user, db]);

  // Load reviews data
  useEffect(() => {
    const loadReviews = async () => {
      if (!user?.id) return;

      try {
        // Query reviews - simple query without orderBy to avoid index requirement
        const reviewsRef = collection(db, "reviews");
        const q = query(
          reviewsRef,
          where("revieweeId", "==", user.id)
        );

        const snapshot = await getDocs(q);
        // Sort client-side if needed
        const rev = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const aDate = new Date(b.createdAt || 0).getTime();
            const bDate = new Date(a.createdAt || 0).getTime();
            return aDate - bDate; // desc order
          })
          .slice(0, 10);

        console.log("✅ Reviews loaded:", rev.length);
        setReviews(rev);
      } catch (error) {
        console.error("❌ Error loading reviews:", error);
        setReviews([]);
      }
    };

    loadReviews();
  }, [user, db]);

  // Fetch recent test results
  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, "jobseekerResults"),
      where("jobSeekerId", "==", user.id),
      orderBy("completedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          jobTitle: data.jobTitle,
          completedDate:
            safeToDate(data.completedAt)?.toISOString()?.split("T")[0] ||
            new Date().toISOString().split("T")[0],
          score: data.totalScore || data.score || 0,
          ...data,
        };
      });
      setRecentTests(results.slice(0, 5)); // Show only recent 5 tests

      // Calculate stats
      let totalScore = 0;
      let validScores = 0;
      let interviewsCount = 0;

      results.forEach(test => {
        const testScore = test.totalScore || test.score || test.interviewScore;
        if (testScore !== undefined && testScore !== null) {
          totalScore += testScore;
          validScores++;
        }
        if (test.interviewScore !== undefined && test.interviewScore !== null) {
          interviewsCount++;
        }
      });

      const avgSuccessRate = validScores > 0 ? Math.round(totalScore / validScores) : 0;

      setStats({
        testsCompleted: results.length,
        interviewsDone: interviewsCount,
        successRate: avgSuccessRate
      });
      setLoadingStats(false);
    });

    return () => unsubscribe();
  }, [user, db]);

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingPicture(true);
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Upload timeout. Please try again.")), 30000)
    );

    try {
      console.log("Uploading profile picture...", file.name, file.size);

      // Upload image to Firebase Storage
      const storageRef = ref(storage, `profile-pictures/${user.id}/${Date.now()}`);
      const uploadPromise = uploadBytes(storageRef, file);
      const uploadResult = await Promise.race([uploadPromise, timeoutPromise]);
      console.log("Upload successful:", uploadResult);

      const downloadURLPromise = getDownloadURL(storageRef);
      const downloadURL = await Promise.race([downloadURLPromise, timeoutPromise]);
      console.log("Got download URL:", downloadURL);

      // Update Firebase Auth profile
      const auth = getAuth(firebaseApp);
      if (auth.currentUser) {
        await updateProfile(auth.currentUser, {
          photoURL: downloadURL,
        });
        console.log("Firebase Auth profile updated");
      }

      // Update Firestore
      const firestorePromise = updateDoc(doc(db, "userProfiles", user.id), {
        profilePictureURL: downloadURL,
      });
      await Promise.race([firestorePromise, timeoutPromise]);
      console.log("Firestore updated");

      // Update local state
      setProfileData((prev) => ({
        ...prev,
        avatar: downloadURL,
      }));

      // Update user context if setUser is available
      if (setUser) {
        setUser((prev) => ({
          ...prev,
          photoURL: downloadURL,
        }));
      }

      console.log("Profile picture upload complete");
      toast.success("Profile picture updated successfully!");
    } catch (error) {
      console.error("Upload error:", error);
      const errorMsg = error.message?.includes("timeout")
        ? "Upload took too long. Please check your connection and try again."
        : "Failed to upload picture. Please try again.";
      toast.error(errorMsg);
    } finally {
      setUploadingPicture(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSaveProfile = async () => {
    if (!user || !editFormData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const profileRef = doc(db, 'userProfiles', user.id);

      // First, check if the profile document exists
      const profileSnapshot = await getDoc(profileRef);

      let updateData;

      if (profileSnapshot.exists()) {
        // Document exists, update it
        const existingData = profileSnapshot.data();

        // Safely update nested resume data structure
        const updatedResumeData = {
          ...existingData.resumeData,
          personalInfo: {
            ...existingData.resumeData?.personalInfo,
            name: editFormData.name,
            location: editFormData.location,
          },
          // Keep existing work history and other data
          workHistory: existingData.resumeData?.workHistory || [],
          skills: existingData.resumeData?.skills || [],
          education: existingData.resumeData?.education || '',
        };

        updateData = {
          name: editFormData.name,
          username: editFormData.username,
          title: editFormData.title,
          location: editFormData.location,
          resumeData: updatedResumeData,
          updatedAt: new Date(),
        };

        await updateDoc(profileRef, updateData);
      } else {
        // Document doesn't exist, create a new one
        updateData = {
          userId: user.id,
          name: editFormData.name,
          username: editFormData.username,
          title: editFormData.title,
          location: editFormData.location,
          resumeData: {
            personalInfo: {
              name: editFormData.name,
              location: editFormData.location,
            },
            workHistory: [],
            education: 'Education not provided',
          },
          resumeUploaded: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        await setDoc(profileRef, updateData);
      }

      // Update local state immediately for UI responsiveness
      setProfileData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          name: editFormData.name,
          username: editFormData.username,
          title: editFormData.title,
          location: editFormData.location,
        };
      });

      setIsEditing(false);

      // Update the Auth context so the Header reflects the new name
      if (updateUserProfile) {
        updateUserProfile({ name: editFormData.name });
      }

      // Show success message
      toast.success('Profile updated successfully!');

      // Track profile edit activity
      trackActivity('profile_edit', {
        displayName: editFormData.name,
        email: editFormData.email,
      });

    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      toast.error(`Failed to update profile: ${errorMessage}. Please try again.`);
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleBannerUpload = async (file) => {
    if (!user || !file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Banner image must be less than 5MB");
      return;
    }

    setUploadingBanner(true);
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Upload timeout. Please try again.")), 30000)
    );

    try {
      const storage = getStorage(firebaseApp);
      const bannerRef = ref(storage, `profile-banners/${user.id}`);

      console.log("Uploading banner file...", file.name, file.size);

      const uploadPromise = uploadBytes(bannerRef, file);
      const uploadResult = await Promise.race([uploadPromise, timeoutPromise]);
      console.log("Upload successful:", uploadResult);

      const downloadURLPromise = getDownloadURL(bannerRef);
      const url = await Promise.race([downloadURLPromise, timeoutPromise]);
      console.log("Got download URL:", url);

      setBannerImageUrl(url);

      // Update Firestore
      const profileRef = doc(db, "userProfiles", user.id);
      const updatePromise = updateDoc(profileRef, {
        bannerImage: url,
        updatedAt: new Date()
      });
      await Promise.race([updatePromise, timeoutPromise]);

      console.log("Firestore updated successfully");
      toast.success("Banner updated successfully!");
      setShowBannerEditModal(false);
    } catch (error) {
      console.error("Error uploading banner:", error);
      const errorMsg = error.message?.includes("timeout")
        ? "Upload took too long. Please check your connection and try again."
        : "Failed to upload banner. Please try again.";
      toast.error(errorMsg);
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleInputChange = (e) => {
    setEditFormData({
      ...editFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleValidateSkills = () => {
    if (!profileData?.resumeData) {
      toast.error("Please upload a resume first to validate your skills.");
      return;
    }

    // Navigate to the new skills validation page
    navigate("/job-seeker/skills-validation");
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.oldPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (passwordData.oldPassword === passwordData.newPassword) {
      toast.error("New password must be different from old password");
      return;
    }

    setPasswordLoading(true);

    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.email) {
        toast.error("User not authenticated");
        return;
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.oldPassword
      );

      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, passwordData.newPassword);

      toast.success("Password updated successfully!");
      setShowPasswordModal(false);
      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Password change error:", error);
      if (error.code === "auth/wrong-password") {
        toast.error("Current password is incorrect");
      } else if (error.code === "auth/requires-recent-login") {
        toast.error("Please log out and log in again to change password");
      } else {
        toast.error("Failed to change password. Please try again.");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-800"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50/20 to-gray-50">
      <div className="mx-auto px-4 py-6" style={{ maxWidth: '1920px' }}>
        {/* Profile Card */}
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          {/* Header Section with Cover Image */}
          <div className="relative group">
            {/* Cover Image / Gradient Background */}
            <div className="h-48 w-full overflow-hidden relative">
              {profileData?.bannerImage || bannerImageUrl ? (
                <img
                  src={bannerImageUrl || profileData.bannerImage}
                  alt="Profile Banner"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-r from-red-900 via-red-800 to-red-600 relative overflow-hidden">
                  <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                </div>
              )}
              {/* Dark overlay on hover for better visibility of edit button */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
            </div>

            {/* Banner Action Buttons */}
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={() => setShowBannerEditModal(true)}
                className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg hover:bg-white hover:scale-105 transition-all text-gray-700 hover:text-red-600 border border-white/50"
                title="Edit Banner"
              >
                <Edit3 className="w-5 h-5" />
              </button>
            </div>

            {/* Profile Picture */}
            <div className="absolute -bottom-16 left-8">
              <div className="relative">
                <div className="w-32 h-32 rounded-2xl bg-white p-1.5 shadow-xl rotate-0 transition-transform hover:rotate-2">
                  <div className="w-full h-full rounded-xl overflow-hidden relative group cursor-pointer bg-gray-100">
                    {profileData?.avatar ? (
                      <img
                        src={profileData.avatar}
                        alt={profileData.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center text-red-700 text-4xl font-bold">
                        {getInitials(profileData?.name)}
                      </div>
                    )}

                    {/* Upload Overlay */}
                    {!isEditing && (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center"
                      >
                        <Edit3 className="w-8 h-8 text-white drop-shadow-lg" />
                      </div>
                    )}

                    {uploadingPicture && (
                      <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center backdrop-blur-sm rounded-xl">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  disabled={uploadingPicture}
                  className="hidden"
                />
              </div>
            </div>

            {/* Manual URL Trigger - Keeping simple for now, can add match HR exactly if requested but structure is key */}
            <div className="absolute -bottom-14 left-44 block animate-fadeIn hidden sm:block">
              <div className="flex gap-2">
                <div className="bg-white/90 backdrop-blur-md px-3 py-2 rounded-xl shadow-sm border border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                  <div className="text-[10px] text-gray-500">Max 5MB • Square</div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="pt-20 px-8 pb-8">
            <div className="flex flex-col md:flex-row justify-between items-start gap-6">
              {/* Left Side: Name & Details */}
              <div className="flex-1 space-y-4">
                <div>
                  <div className="flex items-center gap-3">
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.name}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, name: e.target.value })
                        }
                        className="text-3xl font-bold text-gray-900 border-b-2 border-red-200 focus:border-red-600 focus:outline-none bg-transparent w-full max-w-md px-1"
                        placeholder="Your Name"
                      />
                    ) : (
                      <div className="flex items-center gap-2">
                        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{profileData?.name}</h1>
                        <VerifiedBadge isVerified={isVerified} size="md" />
                      </div>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={editFormData.title}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, title: e.target.value })
                        }
                        className="text-lg text-gray-600 border-b border-gray-300 focus:border-red-500 focus:outline-none bg-transparent w-48 px-1"
                        placeholder="Job Title"
                      />
                    </div>
                  ) : (
                    <p className="text-lg text-gray-600 font-medium mt-1">
                      {profileData?.title}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-red-500" />
                    {isEditing ? (
                      <input
                        type="text"
                        value={editFormData.location}
                        onChange={(e) =>
                          setEditFormData({ ...editFormData, location: e.target.value })
                        }
                        className="border-b border-gray-300 focus:border-red-500 focus:outline-none bg-transparent px-1 min-w-[150px]"
                        placeholder="Location"
                      />
                    ) : (
                      <span>{profileData?.location}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-red-500" />
                    <span>Joined {formatDate(profileData?.joinDate)}</span>
                  </div>
                  {profileData?.resumeData?.personalInfo?.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-red-500" />
                      <span>{profileData.resumeData.personalInfo.email}</span>
                    </div>
                  )}
                  {profileData?.resumeData?.personalInfo?.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-red-500" />
                      <span>{profileData.resumeData.personalInfo.phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Side: Actions */}
              <div className="flex flex-col gap-3 w-full sm:w-auto mt-6 md:mt-0">
                {isEditing ? (
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveProfile}
                      className="flex-1 sm:flex-none px-6 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-red-500/30 hover:shadow-red-500/50 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      Save Changes
                    </button>
                    <button
                      onClick={() => { setIsEditing(false); setEditFormData(profileData); }}
                      className="flex-1 sm:flex-none px-6 py-2.5 bg-white text-gray-700 rounded-xl text-sm font-bold border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="flex gap-3">
                      <button
                        onClick={handleEditToggle}
                        className="flex-1 px-5 py-2.5 bg-white text-gray-700 rounded-xl text-sm font-bold border-2 border-gray-100 hover:border-red-100 hover:bg-red-50/50 hover:text-red-700 shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
                      >
                        <Edit3 className="w-4 h-4" />
                        Edit Profile
                      </button>
                      <button
                        onClick={() => setShowPasswordModal(true)}
                        className="flex-1 px-5 py-2.5 bg-white text-gray-700 rounded-xl text-sm font-bold border-2 border-gray-100 hover:border-gray-300 hover:bg-gray-50 shadow-sm hover:shadow transition-all flex items-center justify-center gap-2"
                      >
                        Password
                      </button>
                    </div>

                    <button
                      onClick={() => navigate("/job-seeker/subscription")}
                      className="w-full px-6 py-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white rounded-xl text-sm font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] transition-all flex items-center justify-center gap-2 relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                      <span className="relative z-10 flex items-center gap-2">Subscription Plan</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Experience */}
          <div className="lg:col-span-2 space-y-6">
            {/* About Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
              <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-2">
                <div className="w-1 h-6 bg-gradient-to-b from-red-600 to-red-800 rounded-full"></div>
                About
              </h2>
              <p className="text-gray-700 text-base leading-relaxed">
                {profileData?.bio || profileData?.resumeData?.professionalInfo?.summary || "No bio added yet. Upload a resume to populate this field."}
              </p>
            </div>

            {/* Experience from Resume */}
            {getWorkHistory().length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-red-100 to-red-50 rounded-xl">
                    <Briefcase className="w-5 h-5 text-red-600" />
                  </div>
                  <span>Work Experience</span>
                  <span className="text-sm font-medium text-gray-500">(from Resume)</span>
                </h2>
                <div className="space-y-5">
                  {getWorkHistory().map((job, index) => (
                    <div key={index} className="border-l-4 border-red-600 pl-5 pb-5 last:pb-0 hover:bg-red-50/30 -ml-1 pl-6 rounded-r-lg transition-all duration-200">
                      <h3 className="font-bold text-gray-900 text-lg">{job.title}</h3>
                      <p className="text-red-600 font-semibold text-base mt-1">{job.company}</p>
                      {job.duration && (
                        <p className="text-gray-500 text-sm mt-1.5 flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {job.duration}
                        </p>
                      )}
                      {job.description && (
                        <p className="text-gray-700 text-sm mt-3 leading-relaxed">
                          {job.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education from Resume */}
            {getEducation().length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                  </div>
                  <span>Education</span>
                </h2>
                <div className="space-y-5">
                  {Array.isArray(getEducation()) ? (
                    getEducation().map((edu, index) => (
                      <div key={index} className="border-l-4 border-blue-600 pl-5 pb-5 last:pb-0 hover:bg-blue-50/30 -ml-1 pl-6 rounded-r-lg transition-all duration-200">
                        <h3 className="font-bold text-gray-900 text-lg">
                          {edu.degree || 'Education'} {edu.field ? `in ${edu.field}` : ''}
                        </h3>
                        <p className="text-blue-600 font-semibold text-base mt-1">{edu.institution}</p>
                        {edu.year && (
                          <p className="text-gray-500 text-sm mt-1.5 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {edu.year}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-600 text-sm">{getEducation()}</p>
                  )}
                </div>
              </div>
            )}

            {/* Projects from Resume */}
            {getProjects().length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl">
                    <FileText className="w-5 h-5 text-purple-600" />
                  </div>
                  <span>Projects</span>
                </h2>
                <div className="space-y-4">
                  {getProjects().map((project, index) => (
                    <div key={index} className="border-2 border-purple-100 rounded-2xl p-5 hover:shadow-lg hover:border-purple-300 transition-all duration-300 bg-gradient-to-br from-white to-purple-50/20">
                      <h3 className="font-bold text-gray-900 text-lg">{project.name || 'Project'}</h3>
                      {project.link && (
                        <a
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:text-purple-700 text-sm font-semibold flex items-center gap-2 mt-2 hover:gap-3 transition-all duration-200"
                        >
                          <LinkIcon className="w-4 h-4" />
                          View Project →
                        </a>
                      )}
                      {project.description && (
                        <p className="text-gray-700 text-sm mt-3 leading-relaxed">{project.description}</p>
                      )}
                      {project.technologies && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {Array.isArray(project.technologies) ? (
                            project.technologies.map((tech, idx) => (
                              <span key={idx} className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 rounded-lg text-xs font-bold border border-purple-200 hover:scale-105 transition-transform">
                                {tech}
                              </span>
                            ))
                          ) : (
                            <span className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-purple-50 text-purple-700 rounded-lg text-xs font-bold border border-purple-200">
                              {project.technologies}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications from Resume */}
            {getCertifications().length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded-xl">
                    <Award className="w-5 h-5 text-yellow-600" />
                  </div>
                  <span>Certifications</span>
                </h2>
                <div className="space-y-3">
                  {getCertifications().map((cert, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border-2 border-yellow-200 hover:border-yellow-400 hover:shadow-md transition-all duration-200 group">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center flex-shrink-0 shadow-md group-hover:scale-110 transition-transform">
                        <Award className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900 text-base">
                          {typeof cert === 'string' ? cert : cert.name || 'Certification'}
                        </p>
                        {cert.issuer && (
                          <p className="text-gray-600 text-sm mt-0.5">{cert.issuer}</p>
                        )}
                        {cert.date && (
                          <p className="text-gray-500 text-xs mt-1.5 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {cert.date}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Languages from Resume */}
            {getLanguages().length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
                <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-xl">
                    <Globe className="w-5 h-5 text-indigo-600" />
                  </div>
                  <span>Languages</span>
                </h2>
                <div className="flex flex-wrap gap-3">
                  {getLanguages().map((lang, index) => (
                    <span
                      key={index}
                      className="px-4 py-2.5 bg-gradient-to-r from-indigo-100 to-indigo-50 text-indigo-700 rounded-xl text-sm font-bold border-2 border-indigo-200 hover:border-indigo-400 hover:scale-105 transition-all duration-200 shadow-sm"
                    >
                      {typeof lang === 'string' ? lang : lang.name || 'Language'}
                      {lang.proficiency && ` - ${lang.proficiency}`}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Top Skills, Experience Added, Reviews */}
          <div className="space-y-6">
            {/* Top Skills Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
              <h2 className="text-2xl font-bold text-gray-900 mb-5 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-red-100 to-red-50 rounded-xl">
                  <Award className="w-5 h-5 text-red-600" />
                </div>
                <span>Top Skills</span>
              </h2>

              <div className="flex flex-wrap gap-2.5">
                {getSkills().length > 0 ? (
                  getSkills().slice(0, 6).map((skill, index) => (
                    <span
                      key={index}
                      className="px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg text-xs font-semibold hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-all cursor-default"
                    >
                      {skill}
                    </span>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No skills added yet</p>
                )}
              </div>
            </div>

            {/* Experience Added Section - Moved or Hidden to match HR layout (Left blank for now) */}
            {/* 
            {experiences.length > 0 && (
               ...
            )} 
            */}

            {/* Reviews Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-300">
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab("reviews")}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${activeTab === "reviews"
                    ? "bg-red-50 text-red-700"
                    : "text-gray-500 hover:bg-gray-50"
                    }`}
                >
                  Active
                </button>
                <button
                  onClick={() => setActiveTab("pending")}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${activeTab === "pending"
                    ? "bg-red-50 text-red-700"
                    : "text-gray-500 hover:bg-gray-50"
                    }`}
                >
                  Pending
                </button>
              </div>

              {activeTab === "reviews" && (
                <div className="space-y-4">
                  {reviews.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <AlertTriangle className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 text-sm font-medium">No reviews yet</p>
                      <p className="text-gray-400 text-xs mt-1">Reviews will appear here once you receive them</p>
                    </div>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.id} className="border-2 border-gray-100 rounded-2xl p-4 hover:border-red-200 hover:shadow-md transition-all duration-200 bg-gradient-to-br from-white to-gray-50/30">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                            {getInitials(review.reviewerName || "User")}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-900 text-sm">
                              {review.reviewerName || "Anonymous"}
                            </h4>
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <Calendar className="w-3 h-3" />
                              {formatDate(review.createdAt)}
                            </p>
                          </div>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed pl-13">
                          {review.comment ||
                            "Great professional to work with. Highly recommended!"}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {activeTab === "pending" && (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <AlertTriangle className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm font-medium">No pending reviews</p>
                  <p className="text-gray-400 text-xs mt-1">Pending reviews will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 transform animate-slideUp border border-gray-100">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-100 to-red-50 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🔒</span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Change Password</h3>
                </div>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      oldPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                  className="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.oldPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, oldPassword: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-gray-700 font-medium"
                    placeholder="Enter current password"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, newPassword: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-gray-700 font-medium"
                    placeholder="Enter new password"
                  />
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Minimum 6 characters required
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                    }
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 text-gray-700 font-medium"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-8">
                <button
                  onClick={handlePasswordChange}
                  disabled={passwordLoading}
                  className="flex-1 px-6 py-3.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl font-bold hover:from-red-700 hover:to-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02]"
                >
                  {passwordLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </span>
                  ) : (
                    "Update Password"
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({
                      oldPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                  className="flex-1 px-6 py-3.5 bg-white text-gray-700 rounded-xl font-bold border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 transition-all duration-200 shadow-md hover:scale-[1.02]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Banner Edit Modal */}
        {showBannerEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
              <button
                onClick={() => setShowBannerEditModal(false)}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-900"
              >
                ✕
              </button>
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center space-x-2">
                <Pencil className="w-5 h-5 text-red-600" />
                <span>Edit Banner</span>
              </h3>

              {bannerImageUrl && (
                <div className="mb-4 relative">
                  <img
                    src={bannerImageUrl}
                    alt="Banner preview"
                    className="w-full h-24 object-cover rounded-lg"
                  />
                </div>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-900 font-semibold">Upload Guidelines:</p>
                <ul className="text-xs text-blue-800 mt-2 space-y-1">
                  <li>• Maximum file size: <strong>5 MB</strong></li>
                  <li>• Recommended dimensions: <strong>1920x540px</strong></li>
                  <li>• Minimum dimensions: <strong>1200x300px</strong></li>
                  <li>• Aspect ratio: <strong>16:9</strong></li>
                </ul>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => bannerFileInputRef.current?.click()}
                  disabled={uploadingBanner}
                  className="w-full py-2 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 flex items-center justify-center space-x-2 transition-colors"
                >
                  {uploadingBanner ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>Upload Banner Image</span>
                    </>
                  )}
                </button>
                <input
                  ref={bannerFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleBannerUpload(file);
                  }}
                  className="hidden"
                />
                <button
                  onClick={() => setShowBannerEditModal(false)}
                  className="w-full py-2 px-4 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default NewProfile;
